import React from 'react';
import { BrazilClock } from '../ui/ClockComponents';

export interface CoreHUDProps {
  isEditingSymbol: boolean;
  setIsEditingSymbol: (val: boolean) => void;
  symbol: string;
  setSymbol: (val: string) => void;
  timeframe: string;
  lastPrice: number | null;
  priceChange: number;
  currentMomentum?: number | null;
  candleCloseTime?: number;
  latencyMs: number | null;
  setRadialMenuPos?: (pos: any) => void;
  extraControls?: React.ReactNode;
}

export const CoreHUD = React.memo<CoreHUDProps>(({
  isEditingSymbol,
  setIsEditingSymbol,
  symbol,
  setSymbol,
  timeframe,
  lastPrice,
  priceChange,
  currentMomentum,
  candleCloseTime,
  latencyMs,
  setRadialMenuPos,
  extraControls,
}) => {
  return (
    <div className="pointer-events-auto flex flex-col gap-2 w-auto max-w-[75vw]">
      {/* Row 1: Asset Name + Period / Timeframe Badge */}
      <div className="flex items-center flex-wrap gap-3 md:gap-4">
        {isEditingSymbol ? (
          <div className="flex items-center gap-2">
            <input
              autoFocus
              type="text"
              defaultValue={symbol}
              onBlur={(e) => {
                setSymbol(e.target.value.toUpperCase());
                setIsEditingSymbol(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setSymbol(e.currentTarget.value.toUpperCase());
                  setIsEditingSymbol(false);
                }
              }}
              className="text-2xl md:text-4xl lg:text-5xl font-extrabold uppercase tracking-[4px] md:tracking-[8px] bg-[#0a0e27] text-[#00d9ff] border-b border-[#00d9ff]/50 outline-none w-48 focus:border-[#00d9ff]"
              list="crypto-symbols-hud"
            />
            <datalist id="crypto-symbols-hud">
              <option value="BTCUSDT">Bitcoin (Cripto)</option>
              <option value="ETHUSDT">Ethereum (Cripto)</option>
              <option value="SOLUSDT">Solana (Cripto)</option>
              <option value="SUIUSDT">SUI/USD (Cripto)</option>
              <option value="TRXUSDT">TRX/USD (Cripto)</option>
              <option value="US100">NASDAQ 100 (Índice)</option>
              <option value="US500">S&P 500 (Índice)</option>
              <option value="AAPL">Apple (Ação EUA)</option>
              <option value="TSLA">Tesla (Ação EUA)</option>
              <option value="NVDA">NVIDIA (Ação EUA)</option>
              <option value="EURUSD">EUR/USD (Forex)</option>
              <option value="XAUUSDT">Ouro (Commodity)</option>
            </datalist>
          </div>
        ) : (
          <div className="flex items-center flex-wrap gap-2 md:gap-3">
            <h1 
              className="text-2xl md:text-4xl lg:text-5xl font-black uppercase tracking-[4px] md:tracking-[8px] bg-gradient-to-r from-[#00d9ff] to-[#0099ff] bg-clip-text text-transparent cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => { setIsEditingSymbol(true); if(setRadialMenuPos) setRadialMenuPos(null); }}
              title="Clique para mudar o ativo"
            >
              {(symbol || "BTCUSDT").toUpperCase()}
            </h1>
            <span className="bg-[#00d9ff]/10 text-[#00d9ff] border border-[#00d9ff]/40 px-3 py-1 text-sm md:text-lg lg:text-xl font-mono font-black rounded-lg shadow-[0_0_15px_rgba(0,217,255,0.25)] select-none uppercase inline-flex items-center animate-pulse">
              {timeframe}
            </span>
            {extraControls}
          </div>
        )}
      </div>

      {/* Row 2: Price & Percentage Change */}
      {lastPrice && (
        <div className="flex flex-col gap-1 mt-1 md:-mt-1 leading-none">
          <div className="flex items-center gap-4 flex-wrap">
            <span className={`text-2xl md:text-4xl lg:text-5xl font-mono font-bold leading-none drop-shadow-[0_0_12px_rgba(0,0,0,0.6)] ${priceChange >= 0 ? 'text-[#00ffa3]' : 'text-[#ff3d67]'}`}>
              ${lastPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className={`text-base md:text-lg font-bold leading-none ${priceChange >= 0 ? 'text-[#00ffa3]' : 'text-[#ff3d67]'}`}>
              {priceChange >= 0 ? '+' : ''}{priceChange.toFixed(2)}%
            </span>
          </div>
          
          {/* Force and Time */}
          {currentMomentum !== undefined && currentMomentum !== null && (
            <div className="flex items-center gap-4 mt-1 text-xs md:text-sm font-mono font-medium drop-shadow-[0_0_8px_rgba(0,0,0,0.8)]">
              <span className="flex items-center gap-1 bg-[#0a0e27]/60 px-2 py-1 rounded border border-[#666666]/30">
                <span className="text-gray-400">FORÇA:</span>
                <span className={currentMomentum >= 0 ? 'text-[#00ffa3]' : 'text-[#ff3d67]'}>
                  {currentMomentum.toFixed(4)}
                </span>
              </span>
            </div>
          )}
        </div>
      )}
      
      {/* Row 3: Candle countdown and Brazil Clock */}
      {lastPrice && (
        <div className="mt-1">
          <BrazilClock closeTime={candleCloseTime} latencyMs={latencyMs} />
        </div>
      )}
    </div>
  );
});
